import qs from 'qs';
import axios from '@/plugins/axios';
import utils from '@/stores/utils';
import $utils from '@/utils';
import { migrateCatalogEntryFormatFilter } from '@/utils/apiFilterParams';
import { executeBulkAction as executeBulk } from '@/utils/bulk';
import { CSV_EXPORT_LIMIT } from '@/constants/csv-export';

const { getStorageKey, loadFiltersFromStorage, saveFiltersToStorage } = utils;

const DEFAULT_FILTERS = { offset: 0, limit: 20, ordering: 'product_name' };

const createState = (options = {}) => {
    return {
        catalogentries: [],
        catalogentries_stats: {},
        catalogentries_stats_loading: false,
        catalogentries_url: "/api/catalogentries/",
        catalogentries_count: 0,
        catalogentries_filters: DEFAULT_FILTERS,
        catalogentries_loaded: false,
        catalogentries_loading: false,
        catalogentries_loading_errors: null,

        catalogentry_archiving: false,
        catalogentry_archiving_errors: null,

        fetch_stats: true,
        cancelTokenSource: null,
        cancelTokenSourceStats: null,

        selected_items: [],
        bulk_action_loading: false,

        _storageOptions: options || {},
    };
};


const mutations = {

    updateCatalogEntries(state, catalogentries) {
        state.catalogentries = catalogentries;
    },
    resetCatalogEntriesList(state) {
        state.catalogentries = [];
    },
    updateCatalogEntriesLoaded(state, loaded) {
        state.catalogentries_loaded = loaded;
    },
    updateCatalogEntriesCount(state, count) {
        state.catalogentries_count = count;
    },
    updateCatalogEntriesLoading(state, loading) {
        state.catalogentries_loading = loading;
    },
    updateCatalogEntriesLoadingErrors(state, errors) {
        state.catalogentries_loading_errors = errors;
    },
    updateCatalogEntriesFilters(state, filters) {
        state.catalogentries_filters = migrateCatalogEntryFormatFilter(filters);
        const { storageKey, fieldsToSave } = state._storageOptions;
        if (storageKey) {
            saveFiltersToStorage(storageKey, state.catalogentries_filters, fieldsToSave);
        }
    },
    updateCatalogEntryArchiving(state, deleting) {
        state.catalogentry_archiving = deleting;
    },
    updateCatalogEntryArchivingErrors(state, errors) {
        state.catalogentry_archiving_errors = errors;
    },
    updateCatalogEntriesUrl(state, url) {
        state.catalogentries_url = url;
    },
    updateCatalogEntriesStats(state, stats) {
        state.catalogentries_stats = stats;
    },
    updateCatalogEntriesStatsLoading(state, loading) {
        state.catalogentries_stats_loading = loading;
    },

    updateFetchStats(state, fetch_stats) {
        state.fetch_stats = fetch_stats;
    },
    updateCancelTokenSource(state, source) {
        state.cancelTokenSource = source;
    },
    updateCancelTokenSourceStats(state, source) {
        state.cancelTokenSourceStats = source;
    },
    updateStorageOptions(state, storage_options) {
        state._storageOptions = storage_options;
    },
    updateSelectedItems(state, items) {
        state.selected_items = items || [];
    },
    updateBulkActionLoading(state, loading) {
        state.bulk_action_loading = loading;
    },
};


const getters = {

};

const actions = {

    async exportCatalogEntriesAsCSV({ commit, dispatch, state }, { filters }) {
        let catalogentries_filters = { ...(filters || {}) };
        catalogentries_filters.offset = 0;
        catalogentries_filters.limit = CSV_EXPORT_LIMIT;
        catalogentries_filters.format = 'csv';
        
        $utils.downloadFile({
            url: state.catalogentries_url,
            params: catalogentries_filters,
        }, $utils.getFilename('catalogEntries'));
    },

    async archiveCatalogEntry({ commit, dispatch, state }, params) {
        commit('updateCatalogEntryArchiving', true);
        commit('updateCatalogEntryArchivingErrors', null);
        try {
            const url = `/api/catalogentries/${params.instance.id}/archive/`;
            const response = await axios.post(url);
            commit('updateCatalogEntryArchiving', false);
            return response.data;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit('updateCatalogEntryArchiving', false);
            commit('updateCatalogEntryArchivingErrors', error.details);
            throw error;
        }
    },

    async saveCatalogEntry({ commit, dispatch, state }, params) {
        commit('updateCatalogEntrySaving', true);

        try {
            let url = '/api/catalogentries/';
            let method = axios.post;
            if (params.instance.id) {
                url = `/api/catalogentries/${params.instance.id}/`;
                method = axios.put;
            }
            if (params.params) {
                const queryparams = qs.stringify(params.params, { arrayFormat: 'repeat' });
                if (queryparams) {
                    url += `?${queryparams}`;
                }
            }
            const response = await method(url, params.instance);
            commit('updateCatalogEntrySaving', false);
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit('updateCatalogEntrySavingErrors', error.details);
            throw error;
        }
    },

    async fetchCatalogEntries({ commit, dispatch, state }, params) {
        // Cancel any pending request
        if (state.cancelTokenSource) {
            state.cancelTokenSource.cancel("New fetch started");
        }

        // Create new cancel token source
        const source = axios.CancelToken.source();
        commit('updateCancelTokenSource', source);

        commit('updateCatalogEntriesLoading', true);
        commit('updateCatalogEntriesLoadingErrors', null);
        try {
            const response = await axios.get(state.catalogentries_url, {
                params: migrateCatalogEntryFormatFilter(state.catalogentries_filters),
                cancelToken: source.token,
            });
            commit('updateCatalogEntries', response.data.results);
            commit('updateCatalogEntriesCount', response.data.count);
            dispatch('fetchCatalogEntriesStats');

            return response.data.payload;
        } catch (xhr_error) {
            // Ignore cancelled requests
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateCatalogEntriesLoadingErrors', error.details);
            throw error;
        } finally {
            if (state.cancelTokenSource === source) {
                commit('updateCatalogEntriesLoading', false);
                commit('updateCatalogEntriesLoaded', true);
                commit('updateCancelTokenSource', null);
            }
        }
    },


    async fetchCatalogEntriesStats({ commit, dispatch, state }, params) {
        if (!state.fetch_stats) {
            return;
        }

        // Cancel any pending request
        if (state.cancelTokenSourceStats) {
            state.cancelTokenSourceStats.cancel("New stats fetch started");
        }

        // Create new cancel token source
        const source = axios.CancelToken.source();
        commit('updateCancelTokenSourceStats', source);
        commit('updateCatalogEntriesStatsLoading', true);

        try {
            const response = await axios.get(state.catalogentries_url + 'stats/', {
                params: migrateCatalogEntryFormatFilter(state.catalogentries_filters),
                cancelToken: source.token,
            });
            commit('updateCatalogEntriesStats', response.data);
            return response.data;
        } catch (xhr_error) {
            // Ignore cancelled requests
            if (axios.isCancel(xhr_error)) {
                return;
            }
            throw utils.handleError(xhr_error);
        } finally {
            if (state.cancelTokenSourceStats === source) {
                commit('updateCatalogEntriesStatsLoading', false);
                commit('updateCancelTokenSourceStats', null);
            }
        }
    },

    async init({ commit, dispatch, state }, params) {
        if (state.cancelTokenSource) {
            state.cancelTokenSource.cancel("canceled");
            commit('updateCancelTokenSource', null);
        }
        if (state.cancelTokenSourceStats) {
            state.cancelTokenSourceStats.cancel("canceled");
            commit('updateCancelTokenSourceStats', null);
        }

        commit('updateCatalogEntriesLoading', true);
        commit('updateCatalogEntriesLoaded', false);
        commit('resetCatalogEntriesList');
        commit('updateCatalogEntriesCount', 0);
        commit('updateCatalogEntriesStats', {});
        commit('updateCatalogEntriesStatsLoading', true);
        commit('updateFetchStats', params.fetch_stats === undefined ? true : params.fetch_stats);
        commit('updateCatalogEntriesUrl', params.url || "/api/catalogentries/");
        
        // Update storage options if provided
        if (params.storageOptions !== undefined) {
            await commit('updateStorageOptions', params.storageOptions);
        }
        
        // Load filters from localStorage if storage is configured
        const storedFilters = state._storageOptions?.storageKey
            ? loadFiltersFromStorage(state._storageOptions?.storageKey, {})
            : {};
        let mergedFilters = {
            ...DEFAULT_FILTERS,
            ...storedFilters,
            ...(params.filters || {}),
        };
        if (params.omitStoredFilterKeys?.length) {
            for (const key of params.omitStoredFilterKeys) {
                delete mergedFilters[key];
            }
            mergedFilters = { ...mergedFilters, ...(params.filters || {}) };
        }
        await commit('updateCatalogEntriesFilters', migrateCatalogEntryFormatFilter(mergedFilters));
    },

    async updateCatalogEntryPrice({ commit, dispatch, state }, { id, unit_amount }) {
        try {
            const normalizedAmount = $utils.normalizeAmountForApi(unit_amount);
            const response = await axios.patch(`/api/catalogentries/${id}/`, {
                unit_amount: normalizedAmount,
            });
            const index = state.catalogentries.findIndex(entry => entry.id === id);
            if (index !== -1) {
                const updatedEntry = { ...state.catalogentries[index], unit_amount: normalizedAmount };
                commit('updateCatalogEntries', [
                    ...state.catalogentries.slice(0, index),
                    updatedEntry,
                    ...state.catalogentries.slice(index + 1)
                ]);
            }
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async updateCatalogEntryVatRate({ commit, dispatch, state }, { id, vat_rate }) {
        try {
            const response = await axios.patch(`/api/catalogentries/${id}/`, { vat_rate });
            const index = state.catalogentries.findIndex(entry => entry.id === id);
            if (index !== -1) {
                const updatedEntry = { ...state.catalogentries[index], vat_rate };
                commit('updateCatalogEntries', [
                    ...state.catalogentries.slice(0, index),
                    updatedEntry,
                    ...state.catalogentries.slice(index + 1)
                ]);
            }
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async updateCatalogEntryCommission({ commit, dispatch, state }, { id, unit_agent_commission }) {
        try {
            const response = await axios.patch(`/api/catalogentries/${id}/`, { unit_agent_commission });
            const index = state.catalogentries.findIndex(entry => entry.id === id);
            if (index !== -1) {
                const updatedEntry = { ...state.catalogentries[index], unit_agent_commission };
                commit('updateCatalogEntries', [
                    ...state.catalogentries.slice(0, index),
                    updatedEntry,
                    ...state.catalogentries.slice(index + 1)
                ]);
            }
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async updateCatalogEntry({ commit, state }, updatedEntry) {
        const index = state.catalogentries.findIndex(entry => entry.id === updatedEntry.id);
        if (index !== -1) {
            commit('updateCatalogEntries', [
                ...state.catalogentries.slice(0, index),
                updatedEntry,
                ...state.catalogentries.slice(index + 1)
            ]);
        }
    },

    async setCatalogEntryActive({ commit, dispatch, state }, { catalogentry, active }) {
        const url = `/api/catalogentries/${catalogentry.id}/active/`;
        const response = await axios.post(url, { active });
        await dispatch('fetchCatalogEntries');
        return response.data;
    },

    async bulkAction({ commit, dispatch, state }, params) {
        commit('updateBulkActionLoading', true);
        try {
            const selectedSnapshot = [...state.selected_items];
            const result = await executeBulk({
                bulkActionUrl: '/api/catalogentries/bulk/',
                action: params.action,
                instances: selectedSnapshot.map((item) => item.id),
                items: selectedSnapshot,
                payload: params.payload || {},
                context: params.context,
                getIdFn:
                    params.getIdFn ||
                    ((entry) => {
                        const product = entry.product_data;
                        if (!product) {
                            return String(entry.id);
                        }
                        const name = (product.name || '').trim();
                        const upc = (product.upc || '').trim();
                        if (name && upc) {
                            return `${name} (${upc})`;
                        }
                        return name || upc || String(entry.id);
                    }),
                successMessage: params.successMessage,
            });
            commit('updateSelectedItems', []);
            await dispatch('fetchCatalogEntries');
            return { ...result, items: selectedSnapshot };
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit('updateBulkActionLoading', false);
        }
    },
};

export default (options = {}) => ({
    namespaced: true,
    state: createState(options),
    mutations,
    actions
});